/**
 * @file    useWidgetOptionSync.ts
 * @scope   SHARED
 * @summary Owns the drawer edits that must reach the widget draft (goal line, Y axis, text block
 *          and title) and publishes them to the global store atomically.
 * @consumes Global store writer (`setState`), widgetOptionSyncReducer
 * @produces Dispatchers for the sections/handlers plus the payload GUID used on save.
 * @sideEffects Publishes the edited widget to the global store.
 * @notes WHY this hook owns the state: these values used to be four `useState` in the orchestrator
 *        that four effects mirrored into the store — two owners for the same data, and each effect
 *        published from its own stale snapshot, so simultaneous edits dropped each other. The
 *        reducer accumulates one pending patch and a single effect publishes it, which removes the
 *        mirroring and makes concurrent edits safe. Behaviour is pinned by
 *        ./useWidgetOptionSync.test.ts — read the `// QUIRK:` cases before changing anything.
 */
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import {
  initialWidgetOptionSyncState,
  widgetOptionSyncReducer,
} from '../../State/widgetOptionSyncReducer';

export interface UseWidgetOptionSyncParams {
  /** Widget currently being edited. */
  customWidget: any;
  /** Global store writer. */
  setState: (partial: any) => void;
}

export interface WidgetOptionSync {
  /** Payload GUID seeded from the saved widget, consumed by the save lifecycle. */
  payLoad: string;
  /** Queues a goal-line change. */
  setGoalValues: (value: any) => void;
  /** Queues a Y-axis change. */
  setYAxisValues: (value: any) => void;
  /** Queues a text-block change. */
  setTextBlockData: (value: any) => void;
  /** Queues a title change. */
  setWidgetTitle: (value: string) => void;
}

export const useWidgetOptionSync = ({
  customWidget,
  setState,
}: UseWidgetOptionSyncParams): WidgetOptionSync => {
  const [state, dispatch] = useReducer(widgetOptionSyncReducer, initialWidgetOptionSyncState);
  const [payLoad, setPayLoad] = useState<string>('');

  /**
   * WHY refs: the publish effect keys on `revision` alone so that several edits committed together
   * produce a single publication. Reading these through refs keeps that dependency array minimal
   * while still publishing against the current widget.
   */
  const customWidgetRef = useRef(customWidget);
  customWidgetRef.current = customWidget;
  const stateRef = useRef(state);
  stateRef.current = state;

  // Seed the payload GUID from the saved widget (mount only).
  useEffect(() => {
    if (customWidget.payload != undefined) {
      setPayLoad(customWidget.payload.DataSetGuid);
    }
  }, []);

  useEffect(() => {
    // `revision === 0` means nothing has been edited yet, so there is nothing to publish.
    if (state.revision === 0) return;

    const { optionsPatch, displayName } = stateRef.current;
    const widget = customWidgetRef.current;

    // One atomic write for every edit queued since the last publication.
    setState({
      customWidget: {
        ...widget,
        ...(displayName !== null ? { displayName } : {}),
        options: { ...widget.options, ...optionsPatch },
      },
    });

    dispatch({ type: 'published', revision: state.revision });
  }, [state.revision]);

  const setGoalValues = useCallback((value: any) => dispatch({ type: 'goalChanged', value }), []);
  const setYAxisValues = useCallback((value: any) => dispatch({ type: 'yAxisChanged', value }), []);
  const setTextBlockData = useCallback(
    (value: any) => dispatch({ type: 'textBlockChanged', value }),
    []
  );
  const setWidgetTitle = useCallback(
    (value: string) => dispatch({ type: 'titleChanged', value }),
    []
  );

  return { payLoad, setGoalValues, setYAxisValues, setTextBlockData, setWidgetTitle };
};
